import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../component/auth/AuthContext';
import { signup } from "../../store/session/action";
import { parseJwt, handleLogError } from '../../utils/Helpers';
import { usernamePattern, emailPattern } from '../../utils/convertUtil'
import { apiErrorText } from '../../utils/apiError';
import styles from './Login.module.scss';
import toggleOpen from '../../assets/image/toggle_open.png';
import toggleClosed from '../../assets/image/toggle_close.png';
import GuestNaviBar from '../Guest/Navigator/GuestNaviBar';
import { useTranslation } from 'react-i18next';

const Signup = () => {
  const Auth = useAuth();
  const isLoggedIn = Auth.userIsAuthenticated();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [usernameIsEmpty, setUsernameIsEmpty] = useState("");
  const [passwordIsEmpty, setPasswordIsEmpty] = useState("");
  const [emailIsEmpty, setEmailIsEmpty] = useState("");

  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  // 문구가 아니라 상태로 들고 있습니다. 예전에는 "password match" 문자열과
  // 비교해 제출을 막았는데, 번역하면 그 가드가 깨집니다.
  // '' | 'match' | 'mismatch'
  const [passwordMatch, setPasswordMatch] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'username') {
      setUsername(value);
      if (!value) {
        setUsernameIsEmpty(t('auth.err.usernameEmpty'));
      } else if (!usernamePattern.test(value)) {
        setUsernameIsEmpty(t('auth.err.usernameInvalid'));
      } else {
        setUsernameIsEmpty("");
      }
    } else if (name === 'password') {
      setPassword(value);
      setPasswordIsEmpty(value ? '' : t('auth.err.passwordEmpty'));

      if (confirmPassword && value !== confirmPassword) {
        setPasswordMatch('mismatch');
      } else if (confirmPassword && value === confirmPassword) {
        setPasswordMatch('match');
      } else {
        setPasswordMatch('');
      }

    } else if (name === 'confirmPassword') {
      setConfirmPassword(value);

      if (value !== password) {
        setPasswordMatch('mismatch');
      } else {
        setPasswordMatch('match');
      }
      if (value === '') {
        setPasswordMatch('');
      }
    } else if (name === 'email') {
      setEmail(value);
      if (!value) {
        setEmailIsEmpty(t('auth.err.emailEmpty'));
      } else if (!emailPattern.test(value)) {
        setEmailIsEmpty(t('auth.err.emailInvalid'));
      } else {
        setEmailIsEmpty("");
      }
    } else if (name === 'company') {
      setCompany(value);
    }
  };

  const togglePasswordVisibility = (field) => {
    if (field === 'password') {
      setShowPassword(!showPassword);
    } else if (field === 'confirmPassword') {
      setShowConfirmPassword(!showConfirmPassword);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setIsError(false);
    setErrorMessage('');

    if (!username) {
      setUsernameIsEmpty(t('auth.err.usernameEmpty'));
    }
    if (!password) {
      setPasswordIsEmpty(t('auth.err.passwordEmpty'));
    }
    if (!email) {
      setEmailIsEmpty(t('auth.err.emailEmpty'));
    }

    if (!confirmPassword || passwordMatch !== 'match') {
      setPasswordMatch('mismatch');
      return;
    }

    if (!username || !password || !email) {
      return;
    }

    const user = { username, password, email, company };

    try {
      const response = await signup(user);
      if (response.status === 200) {
        const accessToken = response.token;
        const data = parseJwt(accessToken);
        const authenticatedUser = { data, accessToken };

        Auth.userLogin(authenticatedUser);

        setUsername('');
        setPassword('');
        setEmail('');
        setCompany('');

        setUsernameIsEmpty('');
        setPasswordIsEmpty('');
        setEmailIsEmpty('');

      } else {
        setIsError(true);
        if (typeof response === 'string') {
          setErrorMessage(response.startsWith('Bad request:') ? response.replace('Bad request: ', '') : response);
        } else {
          setErrorMessage(apiErrorText(response, 'auth.err.signupError'));
        }
      }
    } catch (error) {
      handleLogError(error);
      setIsError(true);
      
      if (error.response) {
        const { data } = error.response;

        if (typeof data === 'string') {
          setErrorMessage(data);
        } else if (data?.errors?.length > 0) {
          // Bean Validation 응답입니다. 필드 단위 문구라 코드가 없습니다.
          setErrorMessage(data.errors[0].defaultMessage);
        } else {
          // 코드가 실려 오면 브라우저 언어에 맞는 문구로, 없으면 백엔드 원문으로
          // 되돌아갑니다. 예전에는 본문을 그대로 JSON.stringify 해서 보여 주는
          // 갈래가 있었는데, 사용자에게는 읽을 수 없는 내용이었습니다.
          setErrorMessage(apiErrorText(error, 'auth.err.signupError'));
        }
      } else {
        setErrorMessage(error.message || t('auth.err.unknown'));
      }
    }
  };

  if (isLoggedIn) {
    navigate("/");
  }

  return (
    <>
      <GuestNaviBar />
      <div className={styles.loginContainer}>
        <div className={styles.loginBox}>
          <h2 className={styles.loginTitle}>{t('auth.signup.title')}</h2>
          <form onSubmit={handleSubmit}>
            <div className={styles.inputContainer}>
              <input
                type="text"
                placeholder={t('auth.field.username')}
                name="username"
                value={username || ""}
                onChange={handleInputChange}
                className={styles.noeye_inputField}
              />
            </div>
            {usernameIsEmpty && (
              <p className={styles.errorMessage}>{usernameIsEmpty}</p>
            )}
            <div className={styles.passwordInputContainer}>
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder={t('auth.field.password')}
                name="password"
                value={password || ""}
                onChange={handleInputChange}
                className={styles.inputField}
              />
              <img
                src={showPassword ? toggleOpen : toggleClosed}
                alt={t('auth.alt.togglePassword')}
                className={styles.togglePassword}
                onClick={() => togglePasswordVisibility('password')}
              />
            </div>
            {passwordIsEmpty && (
              <p className={styles.errorMessage}>{passwordIsEmpty}</p>
            )}
            <div className={styles.passwordInputContainer}>
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                placeholder={t('auth.field.confirmPassword')}
                name="confirmPassword"
                value={confirmPassword || ""}
                onChange={handleInputChange}
                className={styles.inputField}
              />
              <img
                src={showConfirmPassword ? toggleOpen : toggleClosed}
                alt={t('auth.alt.togglePassword')}
                className={styles.togglePassword}
                onClick={() => togglePasswordVisibility('confirmPassword')}
              />
            </div>
            <p className={passwordMatch === 'match' ? styles.matchMessage : styles.errorMessage}>
              {passwordMatch ? t(`auth.err.password${passwordMatch === 'match' ? 'Match' : 'Mismatch'}`) : ''}
            </p>
            <div className={styles.inputContainer}>
              <input
                type="email"
                placeholder={t('auth.field.email')}
                name="email"
                value={email || ""}
                onChange={handleInputChange}
                className={styles.noeye_inputField}
              />
            </div>
            {emailIsEmpty && (
              <p className={styles.errorMessage}>{emailIsEmpty}</p>
            )}
            <div className={styles.inputContainer}>
              <input
                type="text"
                placeholder={t('auth.field.company')}
                name="company"
                value={company || ""}
                onChange={handleInputChange}
                className={styles.noeye_inputField}
              />
            </div>
            <button type="submit" className={styles.loginButton}>{t('auth.signup.button')}</button>
            {isError && <p className={styles.errorMessage} style={{ marginTop: '10px' }}>{errorMessage}</p>}
            <p className={styles.signUpText}>
              {t('auth.signup.haveAccount')}{' '}
              <NavLink to="/login" className={styles.signUpLink}>{t('auth.signup.login')}</NavLink>
            </p>
          </form>
        </div>
      </div>
    </>
  );
}

export default Signup;